import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { Badge, Button, Layout, PageHeader, Row, Col, Tabs, Tooltip, message } from 'antd'
import TransactionDetailsModal from '@components/Popup/TransactionDetailsModal'
import { tranzila } from '@services/api'
import { normalizeTransactionItems } from '../utils'
import SearchBar from './SearchBar'
import List from './List'
import Unresolved from './Unresolved'
import * as styles from './style.scss'
import {
    DEFAULT_PER_PAGE,
    buildDocumentFilterPayload,
    buildDocumentsPayload,
    cloneDocumentFilters,
    documentsExportError,
    documentsFileName,
    initialDocumentFilters,
    normalizeProductOptions,
    transactionItemsRequest
} from './utils'

// Admin browser over the LOCAL Tranzila cache (tranzila_transaction_documents).
//
// One table row = one cache row = one transaction, with its primary and cancellation documents
// nested inside it. Listing never touches Tranzila: only the per-click PDF action does.
//
// Server-side filtering and pagination throughout -- the data flow follows ReplacedCards, not
// the sibling Tranzila transactions page, which fetches everything and filters in the browser.
// Local state is enough; nothing here needs to survive navigation.
class TranzilaDocuments extends Component<any, any> {
    // Generation counter + unmount flag: filters and pagination overlap easily, and a slow
    // earlier response must never overwrite a newer one.
    fetchSeq = 0
    unmounted = false
    // In-flight guard outside state, like Documents/List's PDF guard: a second click is rejected
    // synchronously, before React has re-rendered the button as disabled.
    exportRequest = false
    // ONE generation for the transaction-details modal, shared by its two requests (the detail
    // row, then its items). Every open and every close bumps it, so a late answer for A can
    // neither replace B nor reopen a closed modal, and A's items can never land under B.
    detailsSeq = 0

    constructor(props) {
        super(props)
        this.state = {
            // DRAFT: what the operator is typing into the SearchBar right now.
            filters: initialDocumentFilters(),
            // APPLIED: the filters the rows below were actually loaded with. null until the
            // first successful response, and the only thing the export is ever built from.
            appliedFilters: null,
            rows: [],
            total: 0,
            page: 1,
            perPage: DEFAULT_PER_PAGE,
            loading: false,
            exporting: false,
            // Dropdown source for the SKU filter. Empty is a perfectly usable state: the field
            // is a tags control, so the operator can always type a code that is not listed.
            skuOptions: [],
            // Dropdown source for the מוצר filter, loaded once per mount like the SKU list. Empty
            // is usable: the page still searches, the product field simply offers nothing.
            productOptions: [],
            // TAB SHELL. The two tabs are separate components with separate state; this page
            // owns only which one is in front.
            //
            // Local state, not a URL query: no Tranzila page in this admin keeps view state in
            // the URL, and a query param here would be the only one -- not worth a routing
            // convention of its own for a two-tab page.
            activeTab: 'documents',
            // LAZY. The unresolved queue is a different, heavier query (it joins users and reads
            // an unindexed column over ~1M rows), so its component is not created -- and
            // therefore fetches nothing -- until the operator asks for that tab. Once opened it
            // STAYS mounted, so switching back and forth keeps both tabs' filters and results.
            unresolvedOpened: false,
            // Filled from the queue's own list response the first time it loads. Deliberately
            // NOT a second count query, and never computed during the Documents page load.
            unresolvedTotal: null,
            // The shared Transactions modal, owned HERE so both tabs open the same instance.
            // detailsTxn stays null until the clicked row's own detail has arrived: the modal is
            // never shown empty or with the previous transaction while the next one loads.
            detailsTxn: null,
            // The row whose detail is in flight; its table shows a spinner instead of the icon.
            detailsLoadingId: null,
            items: [],
            itemsLoading: false,
            itemsError: false
        }
    }

    componentDidMount() {
        this.fetchRows(1)
        this.fetchSkuOptions()
        this.fetchProductOptions()
    }

    // Once per mount. Never refetched on Search, Reset, pagination or export: the list changes
    // when a product is added, not when an operator types.
    fetchSkuOptions = () => {
        tranzila
            .getDocumentsSkuOptions()
            .then(res => {
                if (this.unmounted) return
                const payload = (res && res.data) || {}
                this.setState({ skuOptions: Array.isArray(payload.data) ? payload.data : [] })
            })
            .catch(() => {
                if (this.unmounted) return
                // Convenience only -- the field stays fully usable by typing, so this is a
                // notice, not an error state, and the SKU control is never disabled.
                message.warning('רשימת המק"טים לא נטענה; ניתן להקליד מק"ט ידנית')
            })
    }

    // Once per mount, like the SKU list: products change when the catalogue does, not per search.
    fetchProductOptions = () => {
        tranzila
            .getDocumentsProductOptions()
            .then(res => {
                if (this.unmounted) return
                const payload = (res && res.data) || {}
                // one option per product id, whatever the response shape
                this.setState({ productOptions: normalizeProductOptions(payload.data) })
            })
            .catch(() => {
                if (this.unmounted) return
                message.warning('רשימת המוצרים לא נטענה; ניתן לסנן לפי מק"ט')
            })
    }

    componentWillUnmount() {
        this.unmounted = true
    }

    fetchRows = (page = this.state.page, filters = this.state.filters) => {
        this.fetchSeq += 1
        const seq = this.fetchSeq
        // The request is built from a SNAPSHOT, arrays included, so a tag added while it is in
        // flight can change neither what was asked for nor what is recorded as applied.
        const requested = cloneDocumentFilters(filters)
        this.setState({ loading: true })

        tranzila
            .getDocuments(buildDocumentsPayload(requested, page, this.state.perPage))
            .then(res => {
                if (this.unmounted || seq !== this.fetchSeq) return
                const payload = (res && res.data) || {}
                // ONE setState, so the snapshot and the rows it describes can never disagree.
                // The seq guard above is what keeps a late response from reviving its filters
                // after a newer one has already painted the table.
                this.setState({
                    rows: Array.isArray(payload.data) ? payload.data : [],
                    total: Number(payload.total) || 0,
                    page: Number(payload.page) || page,
                    perPage: Number(payload.per_page) || this.state.perPage,
                    appliedFilters: cloneDocumentFilters(requested),
                    loading: false
                })
            })
            .catch(err => {
                if (this.unmounted || seq !== this.fetchSeq) return
                // Only `loading` moves: the table, its total and its applied snapshot all stay
                // as they were, so a failed search never becomes the export's source.
                this.setState({ loading: false })
                const data = err && err.response && err.response.data
                // Keep whatever is already on screen; a failed refresh should not blank the page.
                message.error(
                    data && data.err_key === 'TRANZILA_DOCUMENTS_INVALID_FILTER'
                        ? 'אחד ממסנני החיפוש אינו תקין'
                        : 'שגיאה בטעינת המסמכים'
                )
            })
    }

    setFilter = (name, value) => {
        this.setState(prev => ({ filters: Object.assign({}, prev.filters, { [name]: value }) }))
    }

    handleSearch = () => {
        this.setState({ page: 1 }, () => this.fetchRows(1))
    }

    // Reset restores the SAME safe default window as the first load. Clearing the dates here
    // would make Reset the single most expensive query on the page; the operator can still
    // clear both pickers by hand if they really want an unbounded scan.
    handleReset = () => {
        const filters = initialDocumentFilters()
        this.setState({ filters, page: 1 }, () => this.fetchRows(1, filters))
    }

    // Another page of the SAME dataset: the applied snapshot is reused, so turning a page can
    // neither apply unsent draft edits nor move what the export would produce.
    handlePageChange = page => this.fetchRows(page, this.state.appliedFilters || this.state.filters)

    // The file must describe the table on screen, so it is built from the APPLIED snapshot and
    // the same payload builder the list uses -- never from draft fields, and never with
    // page/per_page, which only the list adds.
    handleExport = async () => {
        const { appliedFilters, loading, total } = this.state
        // Synchronous and before any await: a disabled button is a hint, not a guarantee.
        if (this.exportRequest || loading || !appliedFilters || !total) return

        this.exportRequest = true
        this.setState({ exporting: true })
        const payload = buildDocumentFilterPayload(appliedFilters)
        try {
            await tranzila.downloadDocumentsFile(payload, documentsFileName(payload))
        } catch (err) {
            // The response is a blob even when it carries a JSON refusal, so it is decoded first.
            message.error(documentsExportError(await tranzila.responseErrorPayload(err)))
        } finally {
            this.exportRequest = false
            if (!this.unmounted) this.setState({ exporting: false })
        }
    }

    handleTabChange = activeTab => {
        this.setState(prev => ({
            activeTab,
            unresolvedOpened: prev.unresolvedOpened || activeTab === 'unresolved'
        }))
    }

    // The queue reports the total it just listed; the badge is that number and nothing else.
    handleUnresolvedTotal = unresolvedTotal => {
        if (!this.unmounted) this.setState({ unresolvedTotal })
    }

    // One click = one primary-key read of the local cache, then at most one existing items
    // request. Nothing else reloads: not the table, the queue, or either options list.
    openTransactionDetails = id => {
        if (id === null || id === undefined || id === '') return
        this.detailsSeq += 1
        const seq = this.detailsSeq
        this.setState({
            detailsTxn: null,
            detailsLoadingId: String(id),
            items: [],
            itemsLoading: false,
            itemsError: false
        })

        tranzila
            .getDocumentTransactionDetails(id)
            .then(res => {
                if (this.unmounted || seq !== this.detailsSeq) return
                const txn = res && res.data && res.data.data
                if (!txn || typeof txn !== 'object') throw new Error('empty detail')
                const itemsRequest = transactionItemsRequest(txn)
                this.setState({ detailsTxn: txn, detailsLoadingId: null, itemsLoading: !!itemsRequest })
                if (itemsRequest) this.loadTransactionItems(seq, itemsRequest)
            })
            .catch(err => {
                if (this.unmounted || seq !== this.detailsSeq) return
                // The modal stays closed; the operator gets a fixed sentence, never the body.
                this.setState({ detailsTxn: null, detailsLoadingId: null })
                const status = err && err.response && err.response.status
                message.error(status === 404 ? 'העסקה לא נמצאה' : 'טעינת פרטי העסקה נכשלה')
            })
    }

    // The Transactions page's own lazy items flow: the existing endpoint, one transaction.
    loadTransactionItems = (seq, itemsRequest) => {
        tranzila
            .getTransactionItems(itemsRequest)
            .then(res => {
                if (this.unmounted || seq !== this.detailsSeq) return
                const payload = res && res.data
                this.setState({ items: normalizeTransactionItems(payload && payload.items), itemsLoading: false })
            })
            .catch(() => {
                if (this.unmounted || seq !== this.detailsSeq) return
                // Only the items section fails; the rest of the modal stays open and usable.
                this.setState({ items: [], itemsLoading: false, itemsError: true })
            })
    }

    closeTransactionDetails = () => {
        this.detailsSeq += 1
        this.setState({
            detailsTxn: null,
            detailsLoadingId: null,
            items: [],
            itemsLoading: false,
            itemsError: false
        })
    }

    render() {
        const {
            filters,
            rows,
            total,
            page,
            perPage,
            loading,
            exporting,
            appliedFilters,
            skuOptions,
            productOptions,
            activeTab,
            unresolvedOpened,
            unresolvedTotal,
            detailsTxn,
            detailsLoadingId,
            items,
            itemsLoading,
            itemsError
        } = this.state
        // Two different reasons to refuse, and only one of them is "there is nothing to export":
        // while a refresh is in flight, or before the first result, `total` describes a table
        // that is on its way out, so it must not decide anything.
        const exportPending = loading || !appliedFilters
        const exportEmpty = !exportPending && !total
        const exportButton = (
            <Button
                htmlType="button"
                icon="download"
                loading={exporting}
                disabled={exporting || exportPending || exportEmpty}
                // pointerEvents lets the wrapper span below receive the hover a disabled button
                // never emits, which is how the sibling Transactions export shows its tooltip.
                style={{ pointerEvents: exportEmpty ? 'none' : 'auto' }}
                onClick={this.handleExport}
            >
                הורדת קובץ
            </Button>
        )
        // Reserved space for the treatment tab: the SAME button, invisible and inert -- disabled, no
        // click handler, out of the tab order and hidden from assistive technology. It exists only
        // so the header keeps its height, and the tab bar under it does not jump on every switch.
        // The treatment queue is not exportable and nothing here suggests it is.
        const reservedAction = (
            <span className={styles.headerReserved} aria-hidden="true">
                <Button htmlType="button" icon="download" disabled tabIndex={-1}>
                    הורדת קובץ
                </Button>
            </span>
        )
        const exportAction = exportEmpty ? (
            <Tooltip title="אין נתונים לייצוא">
                <span style={{ display: 'inline-block' }}>{exportButton}</span>
            </Tooltip>
        ) : (
            exportButton
        )

        return (
            <Layout style={{ padding: '0 1rem' }}>
                {/* The page's top actions. A flex row, not absolute positioning: in RTL the header
                    takes the start (right) and the download the end (upper-left), and the pair wraps
                    onto two lines at narrow widths instead of overlapping. The download belongs to
                    the מסמכים tab, so the treatment tab shows none -- it is not exportable. */}
                <Row type="flex" justify="space-between" align="middle">
                    <Col className={styles.headerTitle}>
                        <PageHeader
                            onBack={() => window.history.back()}
                            title="מסמכים חשבונאיים"
                            // The Documents count, reserved -- not removed -- on the treatment tab,
                            // exactly like the download: the header keeps one geometry across tabs.
                            subTitle={
                                <span
                                    className={activeTab === 'documents' ? undefined : styles.headerReserved}
                                    aria-hidden={activeTab === 'documents' ? undefined : 'true'}
                                >
                                    {`נמצאו ${total} רשומות`}
                                </span>
                            }
                        />
                    </Col>
                    <Col className={styles.headerActions}>
                        {activeTab === 'documents' ? exportAction : reservedAction}
                    </Col>
                </Row>
                {/* The tab BAR carries the page gutter; the panes do not, so every Row inside
                    keeps the exact `0 2rem` padding it had before the tabs existed. */}
                {/* styles.tabs carries the scroll fix and the tab-bar gutter; see style.scss. */}
                <Tabs className={styles.tabs} type="card" activeKey={activeTab} onChange={this.handleTabChange}>
                    {/* The ordinary Documents page, unchanged: same state, same handlers, same
                        rows. Only its wrapper moved. */}
                    <Tabs.TabPane tab="מסמכים" key="documents">
                        <Row gutter={10} style={{ padding: '0 2rem' }}>
                            <Col>
                                <SearchBar
                                    filters={filters}
                                    skuOptions={skuOptions}
                                    productOptions={productOptions}
                                    setFilter={this.setFilter}
                                    onSearch={this.handleSearch}
                                    onReset={this.handleReset}
                                    loading={loading}
                                />
                            </Col>
                        </Row>
                        <Row gutter={10} style={{ padding: '0 2rem' }}>
                            <Col style={{ minWidth: 0 }}>
                                <List
                                    rows={rows}
                                    loading={loading}
                                    page={page}
                                    perPage={perPage}
                                    total={total}
                                    onPageChange={this.handlePageChange}
                                    onOpenTransactionDetails={this.openTransactionDetails}
                                    detailsLoadingId={detailsLoadingId}
                                />
                            </Col>
                        </Row>
                    </Tabs.TabPane>

                    <Tabs.TabPane
                        key="unresolved"
                        tab={
                            <span>
                                נדרש טיפול
                                {unresolvedTotal === null ? null : (
                                    <Badge
                                        count={unresolvedTotal}
                                        showZero
                                        style={{ backgroundColor: unresolvedTotal ? '#f5222d' : '#52c41a' }}
                                    />
                                )}
                            </span>
                        }
                    >
                        {/* Rendered only after the tab has been opened at least once, and never
                            unrendered afterwards: lazy on the way in, sticky on the way back. */}
                        {unresolvedOpened ? (
                            <Unresolved
                                onTotal={this.handleUnresolvedTotal}
                                onOpenTransactionDetails={this.openTransactionDetails}
                                detailsLoadingId={detailsLoadingId}
                            />
                        ) : null}
                    </Tabs.TabPane>
                </Tabs>
                {/* ONE instance for both tabs, the Transactions page's own component, unchanged.
                    It does not know which tab opened it. */}
                <TransactionDetailsModal
                    visible={!!detailsTxn}
                    txn={detailsTxn}
                    items={items}
                    itemsLoading={itemsLoading}
                    itemsError={itemsError}
                    onClose={this.closeTransactionDetails}
                />
            </Layout>
        )
    }
}

export default withRouter(TranzilaDocuments)
